import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type AccountStatus = "active" | "suspended" | "pending_review";

export interface GovernedAccount {
  id: string;
  username: string;
  full_name: string | null;
  business_name: string | null;
  avatar_url: string | null;
  user_type: string | null;
  portal_id: string | null;
  city: string | null;
  verification_status: string | null;
  account_status: AccountStatus;
  created_at: string | null;
}

export interface ModerationAction {
  id: string;
  actor_id: string;
  subject_type: string;
  subject_id: string | null;
  subject_label: string | null;
  action: string;
  reason: string | null;
  created_at: string;
}

/** Accounts admins can approve, suspend or restore. Never selects banking columns. */
export const useGovernedAccounts = () =>
  useQuery({
    queryKey: ["admin-governed-accounts"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select(
          "id, username, full_name, business_name, avatar_url, user_type, portal_id, city, verification_status, account_status, created_at",
        )
        .order("created_at", { ascending: false })
        .limit(500);
      if (error) throw error;
      return (data ?? []) as unknown as GovernedAccount[];
    },
  });

export const useModerationLog = () =>
  useQuery({
    queryKey: ["admin-moderation-log"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("moderation_actions")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(200);
      if (error) throw error;
      return (data ?? []) as unknown as ModerationAction[];
    },
  });

export const useLogModerationAction = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (entry: {
      subject_type: string;
      subject_id?: string | null;
      subject_label?: string | null;
      action: string;
      reason?: string | null;
    }) => {
      const { data: auth } = await supabase.auth.getUser();
      if (!auth.user) throw new Error("Not signed in");
      const { error } = await supabase
        .from("moderation_actions")
        .insert({ ...entry, actor_id: auth.user.id } as never);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-moderation-log"] }),
  });
};

export const useSetAccountStatus = () => {
  const qc = useQueryClient();
  const log = useLogModerationAction();
  return useMutation({
    mutationFn: async ({
      account,
      status,
      reason,
    }: {
      account: GovernedAccount;
      status: AccountStatus;
      reason?: string;
    }) => {
      const { error } = await supabase
        .from("profiles")
        .update({ account_status: status } as never)
        .eq("id", account.id);
      if (error) throw error;
      await log.mutateAsync({
        subject_type: "account",
        subject_id: account.id,
        subject_label: account.business_name || account.full_name || account.username,
        action: status === "active" ? "account_activated" : status === "suspended" ? "account_locked" : "account_flagged",
        reason: reason ?? null,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-governed-accounts"] });
      qc.invalidateQueries({ queryKey: ["agency-members"] });
    },
  });
};

export interface AdminEvent {
  id: string;
  title: string;
  status: string;
  event_date: string;
  city: string | null;
  venue_name: string | null;
  owner_type: string;
  flyer_url: string | null;
  created_at: string;
}

export const useAdminEvents = () =>
  useQuery({
    queryKey: ["admin-events"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("events")
        .select("id, title, status, event_date, city, venue_name, owner_type, flyer_url, created_at")
        .order("created_at", { ascending: false })
        .limit(300);
      if (error) throw error;
      return (data ?? []) as AdminEvent[];
    },
  });

export const useSetEventStatus = () => {
  const qc = useQueryClient();
  const log = useLogModerationAction();
  return useMutation({
    mutationFn: async ({ event, status, reason }: { event: AdminEvent; status: string; reason?: string }) => {
      const { error } = await supabase.from("events").update({ status }).eq("id", event.id);
      if (error) throw error;
      await log.mutateAsync({
        subject_type: "event",
        subject_id: event.id,
        subject_label: event.title,
        action: `event_${status}`,
        reason: reason ?? null,
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-events"] }),
  });
};
